/**
 * superadminApi.js — Servicio de API para el Command Center SuperAdmin de CONEXO POS.
 * - Token guardado bajo clave `superadmin_token` (NO mezcla con usuario normal).
 * - Interceptor de auth inyecta `Authorization: Bearer <token>` en /api/superadmin/*.
 * - URLs alineados con el backend FastAPI (prefijo /superadmin).
 */
import axios from "axios";

// Normalizar BASE_URL (quitar /api del final si lo tiene)
const _raw = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BASE_URL = _raw.replace(/\/api\/?$/, "");
const SA_TOKEN_KEY = "superadmin_token";

// ── Helpers de token ─────────────────────────────────────────────────────────
export const getSAToken = () => localStorage.getItem(SA_TOKEN_KEY);
export const setSAToken = (t) => localStorage.setItem(SA_TOKEN_KEY, t);
export const clearSAToken = () => localStorage.removeItem(SA_TOKEN_KEY);

// ── Instancia Axios dedicada ─────────────────────────────────────────────────
const superadminApi = axios.create({
    baseURL: `${BASE_URL}/api/superadmin`,
    headers: { "Content-Type": "application/json" },
    timeout: 20000,
});

// Interceptor: inyectar Bearer token
superadminApi.interceptors.request.use(
    (config) => {
        const token = getSAToken();
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor: logout automático en 401/403
superadminApi.interceptors.response.use(
    (res) => res,
    (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            clearSAToken();
            window.location.href = "/superadmin/login";
        }
        return Promise.reject(error);
    }
);

// ── AUTH ─────────────────────────────────────────────────────────────────────

/** Login del SuperAdmin */
export const superadminLogin = async (email, password) => {
    const res = await axios.post(`${BASE_URL}/api/superadmin/login`, { email, password });
    const { access_token, superadmin } = res.data;
    if (!access_token) throw new Error("Acceso denegado: respuesta inválida del servidor.");
    setSAToken(access_token);
    return superadmin;
};

// ── TENANTS (RESTAURANTES) ────────────────────────────────────────────────────

/** Lista paginada de tenants */
export const fetchTenants = (params = {}) =>
    superadminApi.get("/tenants", { params }).then((r) => r.data);

/** Alias compatible con código anterior */
export const fetchCompanies = (params = {}) => fetchTenants(params);

/** Detalle de un tenant */
export const fetchTenant = (company_id) =>
    superadminApi.get(`/tenants/${company_id}`).then((r) => r.data);

/** Alias compatible */
export const fetchCompany = (company_id) => fetchTenant(company_id);

/** Crear tenant */
export const createTenant = (data) =>
    superadminApi.post("/tenants", data).then((r) => r.data);

/** Editar tenant */
export const updateTenant = (company_id, data) =>
    superadminApi.put(`/tenants/${company_id}`, data).then((r) => r.data);

/** Suspender tenant */
export const suspendTenant = (company_id, reason = "") =>
    superadminApi.post(`/tenants/${company_id}/suspend`, { reason }).then((r) => r.data);

/** Reactivar tenant */
export const reactivateTenant = (company_id) =>
    superadminApi.post(`/tenants/${company_id}/reactivate`).then((r) => r.data);

/** Eliminar tenant */
export const deleteTenant = (company_id, force = false) =>
    superadminApi.delete(`/tenants/${company_id}?force=${force}`).then((r) => r.data);

/** Impersonar (entrar como admin del tenant) */
export const impersonateTenant = (company_id) =>
    superadminApi.post(`/tenants/${company_id}/impersonate`).then((r) => r.data);

/** Alias compatible con código anterior */
export const impersonateCompany = (company_id) => impersonateTenant(company_id);

/** Cambiar plan de un tenant */
export const changeTenantPlan = (company_id, plan_id, reason = "") =>
    superadminApi.put(`/tenants/${company_id}/plan`, { plan_id, reason }).then((r) => r.data);

/** Alias compatible */
export const changePlan = (company_id, plan_id, reason = "") =>
    changeTenantPlan(company_id, plan_id, reason);

/** Feature flags de un tenant */
export const setTenantFlags = (company_id, flags) =>
    superadminApi.put(`/tenants/${company_id}/flags`, { flags }).then((r) => r.data);

// ── PLANES ────────────────────────────────────────────────────────────────────

/** Lista de planes */
export const fetchPlans = () =>
    superadminApi.get("/plans").then((r) => r.data);

/** Sembrado inicial de planes */
export const initPlans = () =>
    superadminApi.post("/plans/init").then((r) => r.data);

/** Crear plan */
export const createPlan = (data) =>
    superadminApi.post("/plans", data).then((r) => r.data);

/** Editar plan */
export const updatePlan = (plan_id, data) =>
    superadminApi.put(`/plans/${plan_id}`, data).then((r) => r.data);

/** Eliminar plan */
export const deletePlan = (plan_id) =>
    superadminApi.delete(`/plans/${plan_id}`).then((r) => r.data);

// ── FEATURE FLAGS ─────────────────────────────────────────────────────────────

/** Flags globales */
export const fetchGlobalFlags = () =>
    superadminApi.get("/feature-flags").then((r) => r.data);

/** Actualizar flags globales */
export const updateGlobalFlags = (flags) =>
    superadminApi.put("/feature-flags", { flags }).then((r) => r.data);

// ── SUSCRIPCIONES ─────────────────────────────────────────────────────────────

/** Lista de suscripciones */
export const fetchSubscriptions = (params = {}) =>
    superadminApi.get("/subscriptions", { params }).then((r) => r.data);

/** Alias */
export const fetchGlobalSubscriptions = (params = {}) => fetchSubscriptions(params);

/** Cambiar status de suscripción */
export const changeStatus = (company_id, status, reason = "") =>
    superadminApi.put(`/subscriptions/${company_id}/status`, { status, reason }).then((r) => r.data);

/** Override de feature en suscripción */
export const setFeatureOverride = (company_id, feature, value, remove = false) =>
    superadminApi.put(`/subscriptions/${company_id}/override`, { feature, value, remove }).then((r) => r.data);

// ── STATS ─────────────────────────────────────────────────────────────────────

/** Stats globales del dashboard */
export const fetchStats = () =>
    superadminApi.get("/stats").then((r) => r.data);

/** Platform stats */
export const fetchPlatformStats = () =>
    superadminApi.get("/platform/stats").then((r) => r.data);

// ── PEDIDOS GLOBALES ──────────────────────────────────────────────────────────

/** Pedidos globales */
export const fetchGlobalOrders = (params = {}) =>
    superadminApi.get("/orders", { params }).then((r) => r.data);

// ── AUDITORÍA ─────────────────────────────────────────────────────────────────

/** Log de auditoría */
export const fetchAuditLog = (params = {}) =>
    superadminApi.get("/audit", { params }).then((r) => r.data).catch(() => ({ items: [], total: 0, page: 1, limit: 50 }));

/** Eventos de seguridad */
export const fetchSecurityEvents = (params = {}) =>
    superadminApi.get("/events/security", { params }).then((r) => r.data).catch(() => ({ items: [], next_cursor: null, total_returned: 0 }));

/** Eventos de acciones del SuperAdmin */
export const fetchAdminEvents = (params = {}) =>
    superadminApi.get("/events/admin", { params }).then((r) => r.data).catch(() => ({ items: [], next_cursor: null, total_returned: 0 }));

// ── DELIVERY ──────────────────────────────────────────────────────────────────

/** Stats de network de delivery */
export const fetchDeliveryStats = () =>
    superadminApi.get("/delivery/stats").then((r) => r.data).catch(() => ({ active_drivers: 0, online_drivers: 0, pending_jobs: 0, delivered_today: 0 }));

/** Snapshot del mapa global */
export const fetchDeliveryMapSnapshot = () =>
    superadminApi.get("/delivery/map/snapshot").then((r) => r.data).catch(() => ({ drivers: [], jobs: [] }));

/** Logs de la red de delivery */
export const fetchDeliveryLogs = (company_id = null) =>
    superadminApi.get("/delivery/logs", { params: { company_id } }).then((r) => r.data).catch(() => []);

/** Lista de repartidores */
export const fetchDrivers = () =>
    superadminApi.get("/delivery/drivers").then((r) => r.data).catch(() => []);

/** Crear repartidor */
export const createDriver = (data) =>
    superadminApi.post("/delivery/drivers", data).then((r) => r.data);

/** Actualizar repartidor (incluye allowed_company_ids) */
export const updateDriver = (driverId, data) =>
    superadminApi.put(`/delivery/drivers/${driverId}`, data).then((r) => r.data);

// ── SISTEMA ────────────────────────────────────────────────────────────────────

/** Logs de sistema */
export const fetchSystemLogs = (limit = 50) =>
    superadminApi.get("/system/logs", { params: { limit } }).then((r) => r.data).catch(() => ({ items: [], total: 0, message: "Módulo disponible próximamente" }));

/** Jobs de sistema */
export const fetchSystemJobs = (status = null) =>
    superadminApi.get("/system/jobs", { params: status ? { status } : {} }).then((r) => r.data).catch(() => ({ items: [], total: 0, message: "Módulo disponible próximamente" }));

// ── DEMO ──────────────────────────────────────────────────────────────────────

/** Resetear datos del tenant demo */
export const resetDemo = () =>
    superadminApi.post("/demo/reset").then((r) => r.data);

export default superadminApi;
